/**
 * Client-side bracket generator. Mirrors the matchmaking logic that
 * /app/backend/bracket.py uses for "real" tournaments so the user gets
 * the same look and connector behaviour when playing with the standalone
 * Bracket Generator page.
 *
 * No backend writes — everything lives in memory + localStorage.
 */

const uid = () =>
  (typeof crypto !== "undefined" && crypto.randomUUID)
    ? crypto.randomUUID()
    : Math.random().toString(36).slice(2) + Date.now().toString(36);

function nextPow2(n) {
  let s = 1;
  while (s < n) s *= 2;
  return s;
}

function roundNameSingleElim(roundIdx, totalRounds) {
  const remaining = totalRounds - roundIdx;
  if (remaining === 1) return "Final";
  if (remaining === 2) return "Semifinal";
  if (remaining === 3) return "Quarterfinal";
  return `Round ${roundIdx + 1}`;
}

export function generateSingleElim(teams) {
  const n = teams.length;
  if (n < 2) throw new Error("Need at least 2 teams");
  const size = nextPow2(n);
  let totalRounds = 0;
  for (let s = size; s > 1; s >>= 1) totalRounds++;
  const seeded = [...teams];
  while (seeded.length < size) seeded.push(null);

  const rounds = [];
  for (let r = 0; r < totalRounds; r++) {
    const matchesInRound = size / (2 ** (r + 1));
    const col = [];
    for (let p = 0; p < matchesInRound; p++) {
      col.push({
        id: uid(),
        round: r,
        round_name: roundNameSingleElim(r, totalRounds),
        bracket: "winners",
        position: p,
        team_a_id: null,
        team_b_id: null,
        team_a_score: null,
        team_b_score: null,
        score_a: null,
        score_b: null,
        winner_id: null,
        status: "scheduled",
        next_match_id: null,
        next_slot: null,
      });
    }
    rounds.push(col);
  }

  // Seed first round
  for (let i = 0; i < rounds[0].length; i++) {
    const a = seeded[i * 2];
    const b = seeded[i * 2 + 1];
    rounds[0][i].team_a_id = a ? a.id : null;
    rounds[0][i].team_b_id = b ? b.id : null;
    if (a && !b) {
      rounds[0][i].winner_id = a.id;
      rounds[0][i].status = "completed";
      rounds[0][i].score_a = 1;
    } else if (b && !a) {
      rounds[0][i].winner_id = b.id;
      rounds[0][i].status = "completed";
      rounds[0][i].score_b = 1;
    }
  }

  // Link next-match pointers
  for (let r = 0; r < totalRounds - 1; r++) {
    for (let i = 0; i < rounds[r].length; i++) {
      const target = rounds[r + 1][Math.floor(i / 2)];
      rounds[r][i].next_match_id = target.id;
      rounds[r][i].next_slot = i % 2 === 0 ? "a" : "b";
    }
  }

  // Propagate BYE winners forward
  for (let r = 0; r < totalRounds - 1; r++) {
    for (const m of rounds[r]) {
      if (m.winner_id && m.next_match_id) {
        const target = rounds[r + 1].find((mm) => mm.id === m.next_match_id);
        if (m.next_slot === "a") target.team_a_id = m.winner_id;
        else target.team_b_id = m.winner_id;
      }
    }
  }

  return rounds.flat();
}

export function generateRoundRobin(teams) {
  const n = teams.length;
  if (n < 2) throw new Error("Need at least 2 teams");
  const lst = teams.map((t) => t.id);
  if (n % 2 === 1) lst.push(null);
  const rounds = lst.length - 1;
  const half = lst.length / 2;
  const out = [];
  let pos = 0;
  for (let r = 0; r < rounds; r++) {
    for (let i = 0; i < half; i++) {
      const a = lst[i];
      const b = lst[lst.length - 1 - i];
      if (a && b) {
        out.push({
          id: uid(),
          round: r,
          round_name: `Round ${r + 1}`,
          bracket: "main",
          position: pos++,
          team_a_id: a,
          team_b_id: b,
          team_a_score: null,
          team_b_score: null,
          score_a: null,
          score_b: null,
          winner_id: null,
          status: "scheduled",
          next_match_id: null,
          next_slot: null,
        });
      }
    }
    // rotate (Berger's algorithm)
    lst.splice(1, 0, lst.pop());
  }
  return out;
}

export function generateGroupAdvancement(teams) {
  // 4-team groups, top 2 advance. Mirrors the real-tournament behaviour.
  const n = teams.length;
  if (n < 4) throw new Error("Group Advancement needs at least 4 teams");
  if (n % 4 !== 0) throw new Error("Group Advancement needs a multiple of 4 teams");
  const groupCount = n / 4;
  const out = [];
  const shuffled = [...teams];
  for (let g = 0; g < groupCount; g++) {
    const groupTeams = shuffled.slice(g * 4, g * 4 + 4);
    // Each pair within the group plays once (round robin of 4 = 6 matches)
    // Simplified: round 1 = 1v4 + 2v3 (group_idx tagged on each match)
    const pairs = [
      [0, 1], [2, 3], [0, 2], [1, 3], [0, 3], [1, 2],
    ];
    pairs.forEach(([i, j], idx) => {
      out.push({
        id: uid(),
        round: idx,
        round_name: `Group ${g + 1} · M${idx + 1}`,
        bracket: "group",
        group_idx: g,
        position: g * 6 + idx,
        team_a_id: groupTeams[i].id,
        team_b_id: groupTeams[j].id,
        team_a_score: null,
        team_b_score: null,
        score_a: null,
        score_b: null,
        winner_id: null,
        status: "scheduled",
        next_match_id: null,
        next_slot: null,
      });
    });
  }
  return out;
}

export function generate(format, teams) {
  switch (format) {
    case "single_elimination":
      return generateSingleElim(teams);
    case "round_robin":
      return generateRoundRobin(teams);
    case "group_advancement":
      return generateGroupAdvancement(teams);
    default:
      throw new Error(`Unknown format: ${format}`);
  }
}

/**
 * Apply a score update + propagate winner to next match (single elim only —
 * round robin and groups don't have a next-match graph).
 */
export function applyScore(matches, matchId, scoreA, scoreB) {
  const a = Number(scoreA);
  const b = Number(scoreB);
  const idx = matches.findIndex((m) => m.id === matchId);
  if (idx < 0) return matches;
  const next = matches.map((m) => ({ ...m }));
  const m = next[idx];
  m.team_a_score = a;
  m.team_b_score = b;
  m.score_a = a;
  m.score_b = b;
  if (Number.isFinite(a) && Number.isFinite(b) && a !== b && m.team_a_id && m.team_b_id) {
    const winnerId = a > b ? m.team_a_id : m.team_b_id;
    m.winner_id = winnerId;
    m.status = "completed";
    // Propagate to next match if defined.
    if (m.next_match_id) {
      const targetIdx = next.findIndex((mm) => mm.id === m.next_match_id);
      if (targetIdx >= 0) {
        if (m.next_slot === "a") next[targetIdx].team_a_id = winnerId;
        else next[targetIdx].team_b_id = winnerId;
        // If editing a previously-completed match, clear stale propagation
        // beyond this point isn't strictly needed for the generator MVP.
      }
    }
  } else {
    m.winner_id = null;
    m.status = "scheduled";
  }
  return next;
}
